<template>
  <div>
    <b-loading can-cancel is-full-page :active="loading.loadPage" />

    <div class="columns is-column-reverse-custom">
      <div class="column">
        <div>
          <b-upload
            multiple
            drag-drop
            v-if="bidder"
            class="custom-upload"
            v-model="data.files"
            :disabled="uploadDisabled"
            @input="addFiles"
          >
            <div class="content has-text-centered custom-upload-content">
              <b-icon class="custom-upload-icon" icon="upload" size="is-medium" />

              <p>Clique aqui ou arraste nessa área para adicionar outro(s) documento(s)</p>
            </div>
          </b-upload>
        </div>

        <div class="panel">
          <div class="panel-heading is-flex is-justify-content-space-between">
            <div>Documentos Enviados</div>

            <b-button
              icon-right="zip-box"
              size="is-small"
              type="is-primary"
              v-if="crier"
              :loading="loading.downloadZip"
              @click.stop.prevent="downloadDocumentsZip()"
              >Download Zip</b-button
            >
          </div>

          <div v-if="!proposals.length && !judgmentDiligenceStep" class="panel-block empty-panel">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <p>Para habilitar o envio de documentos, é necessário enviar proposta(s).</p>
              </div>
            </section>
          </div>

          <div
            v-else-if="!documents.length && bidder && !judgmentDiligenceStep"
            class="panel-block empty-panel"
          >
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <p>Nenhum documento enviado.</p>
              </div>
            </section>
          </div>

          <b-table scrollable v-else class="documents-table" :data="documents">
            <template v-slot:empty>
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>Nenhum documento enviado.</p>
                </div>
              </section>
            </template>

            <b-table-column class="column-id" numeric label="ID" v-slot="props">{{
              props.index + 1
            }}</b-table-column>

            <b-table-column class="column-document" label="Documento" v-slot="props">
              <a :href="props.row.file | fileServer" tabindex="-1" download>
                {{ props.row.fileName }}
              </a>
            </b-table-column>

            <b-table-column
              cell-class="column-type"
              header-class="column-type"
              label="Tipo"
              v-slot="props"
            >
              <div v-if="props.row.createdAt">
                <b-tooltip
                  class="tooltips-tag"
                  :label="validDocument(props.row.validity) ? 'Válido' : 'Vencido'"
                  :type="validDocument(props.row.validity) ? 'is-success' : 'is-danger'"
                  v-if="props.row.validity"
                >
                  <b-tag
                    class="custom-tag"
                    :type="validDocument(props.row.validity) ? 'is-success' : 'is-danger'"
                  >
                    {{ new Date(props.row.validity).toLocaleDateString('pt-BR') }}
                  </b-tag>
                </b-tooltip>

                <b-tooltip
                  v-if="props.row.origin"
                  class="tooltips-tag"
                  type="is-primary"
                  :label="
                    props.row.origin === 'PROPOSTAS'
                      ? 'Entrega de Propostas'
                      : props.row.origin === 'HABILITACAO'
                      ? 'Habilitação'
                      : props.row.origin === 'JULGAMENTO'
                      ? 'Diligência de Julgamento'
                      : 'Habilitação da Adesão'
                  "
                >
                  <b-tag class="custom-tag" type="is-primary">
                    {{
                      props.row.origin === 'PROPOSTAS'
                        ? 'P'
                        : props.row.origin === 'HABILITACAO'
                        ? 'H'
                        : props.row.origin === 'JULGAMENTO'
                        ? 'J'
                        : 'HA'
                    }}
                  </b-tag>
                </b-tooltip>

                <span>{{ props.row.type }}</span>
              </div>

              <b-field v-else-if="judgmentDiligenceStep" expanded>
                <b-input
                  v-model="props.row.type"
                  placeholder="Ex.: Certidão negativa de débitos"
                  maxlength="225"
                />
              </b-field>

              <b-field v-else expanded>
                <b-autocomplete
                  open-on-focus
                  class="select-type"
                  placeholder="Selecione um tipo de documento"
                  tabindex="-1"
                  v-model="props.row.type"
                  :data="filteredDocumentTypes"
                  @keydown.native.prevent
                  @select="option => selectType(props.index, option.type)"
                >
                  <template slot="empty">Nenhum documento requerido</template>

                  <template slot-scope="props">
                    <div class="media">
                      <div class="media-content document-type">
                        {{ props.option.type }}
                      </div>
                    </div>
                  </template>
                </b-autocomplete>
              </b-field>
            </b-table-column>

            <b-table-column
              class="column-name"
              label="Nome"
              v-slot="props"
              :visible="displayNameColumn"
            >
              <b-input
                class="input-name"
                custom-class="uppercase"
                placeholder="Insira um nome"
                maxlength="225"
                :ref="`name-${props.index}`"
                v-if="customDocument(props.row.type)"
                v-model="props.row.name"
                @blur="event => updateTypeName(props.index, props.row.type, event.target.value)"
              ></b-input>
            </b-table-column>

            <b-table-column
              centered
              class="column-date"
              label="Enviado em"
              v-slot="props"
              :visible="documents.some(document => document.createdAt)"
            >
              {{ props.row.createdAt | formatDate }}
            </b-table-column>

            <b-table-column centered label="Aceito" v-slot="props" :visible="displayAcceptedColumn">
              <b-icon
                custom-size="mdi-24px"
                icon="check"
                size="is-medium"
                type="is-success"
                v-if="props.row.accept === true"
              />
              <b-icon
                custom-size="mdi-24px"
                icon="close"
                type="is-danger"
                v-if="props.row.accept === false"
              />
            </b-table-column>

            <b-table-column centered label="Opções" v-slot="props" :visible="displayOptionsColumn">
              <div class="options-column">
                <button
                  v-if="displayDeleteButton(props.row)"
                  class="button is-danger delete-button"
                  tabindex="-1"
                  :disabled="disable.delete"
                  @click.prevent.stop="deleteDocument(props.index, props.row.id)"
                >
                  <b-icon icon="close-circle" />
                </button>

                <div
                  class="is-flex is-justify-content-center"
                  v-if="crier && props.row.accept === null"
                >
                  <button
                    class="button is-primary actions-buttons"
                    :disabled="disable.accept"
                    @click.prevent.stop="evaluateDocument(props.row.id, 'accept')"
                  >
                    Aceitar
                  </button>

                  <button
                    class="button is-danger actions-buttons"
                    :disabled="disable.refuse"
                    @click.prevent.stop="evaluateDocument(props.row.id, props.row.type, 'refuse')"
                  >
                    Recusar
                  </button>
                </div>

                <div
                  v-else-if="crier && judgmentDiligenceStep && props.row.accept !== null"
                  class="is-flex is-justify-content-center"
                >
                  <button
                    class="button is-danger is-small is-outlined offers-button"
                    :disabled="disable.resetEvaluation"
                    @click.prevent.stop="resetDocumentEvaluation(props.row.id)"
                  >
                    Desfazer
                  </button>
                </div>
              </div>
            </b-table-column>

            <template v-if="displayTableFooter" v-slot:footer>
              <div class="columns">
                <div v-if="bidder" :class="calculateUploadColumn">
                  <b-progress
                    show-value
                    v-if="bidder"
                    class="footer-elements ml-1"
                    format="percent"
                    type="is-info"
                    size="is-medium"
                    :value="uploadProgressValue"
                  ></b-progress>
                </div>

                <div :class="calculateButtonsColumn">
                  <div :class="bidder ? 'has-text-right' : 'has-text-centered'">
                    <b-tag
                      v-if="bidder && documentToSend"
                      class="footer-elements documents-warning-tag ml-1"
                      type="is-danger"
                      >Há documento(s) não enviado(s)</b-tag
                    >
                    <button
                      class="button is-primary footer-elements ml-1"
                      v-if="bidder && documents.length"
                      :disabled="uploadDisabled"
                      :class="{ ' is-loading': loading.send }"
                      @click.prevent.stop="saveDocuments()"
                    >
                      {{ sendButtonLabel }}
                    </button>

                    <div
                      v-if="crier && !judgmentDiligenceStep && displayBatchQualification"
                      class="qualification-actions__split footer-elements ml-1"
                    >
                      <button
                        class="button is-primary qualification-actions__main"
                        :class="{ 'is-loading': loading.batch }"
                        :disabled="disableQualificationButton || loading.batch"
                        @click.prevent.stop="openModal('habilitar')"
                      >
                        Habilitar
                      </button>
                      <b-dropdown
                        position="is-bottom-left"
                        class="qualification-actions__menu"
                        aria-role="list"
                        :disabled="disableQualificationButton || loading.batch"
                      >
                        <button
                          slot="trigger"
                          class="button is-primary qualification-actions__toggle"
                          type="button"
                          :disabled="disableQualificationButton || loading.batch"
                          aria-label="Mais opções de habilitação"
                        >
                          <b-icon icon="menu-down" size="is-small" />
                        </button>
                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="disableQualificationButton || loading.batch"
                          @click="openModal('habilitar-lote')"
                        >
                          Habilitar em Todos os Itens
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>

                    <button
                      v-else-if="crier && !judgmentDiligenceStep"
                      class="button is-primary footer-elements ml-1"
                      :disabled="disableQualificationButton"
                      @click.prevent.stop="openModal('habilitar')"
                    >
                      Habilitar
                    </button>

                    <div
                      v-if="crier && !judgmentDiligenceStep && displayBatchQualification"
                      class="qualification-actions__split footer-elements ml-1"
                    >
                      <button
                        class="button is-danger qualification-actions__main"
                        :class="{ 'is-loading': loading.batch }"
                        :disabled="disableIncapacitateButton || loading.batch"
                        @click.prevent.stop="openModal('inabilitar')"
                      >
                        Inabilitar
                      </button>
                      <b-dropdown
                        position="is-bottom-left"
                        class="qualification-actions__menu"
                        aria-role="list"
                        :disabled="disableIncapacitateButton || loading.batch"
                      >
                        <button
                          slot="trigger"
                          class="button is-danger qualification-actions__toggle"
                          type="button"
                          :disabled="disableIncapacitateButton || loading.batch"
                          aria-label="Mais opções de inabilitação"
                        >
                          <b-icon icon="menu-down" size="is-small" />
                        </button>
                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="disableIncapacitateButton || loading.batch"
                          @click="openModal('inabilitar-lote')"
                        >
                          Inabilitar em Todos os Itens
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>

                    <button
                      v-else-if="crier && !judgmentDiligenceStep"
                      class="button is-danger footer-elements ml-1"
                      :disabled="disableIncapacitateButton"
                      @click.prevent.stop="openModal('inabilitar')"
                    >
                      Inabilitar
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <b-modal
      has-modal-card
      ref="justificationModal"
      :active.sync="justification.exibir"
      @justificate="evaluateBidder"
    >
      <AppJustification
        v-if="justification.exibir"
        :failText="justification.textoFalha"
        :displayFailButton="justification.exibirFalha"
        :displaySuccessButton="justification.exibirSucesso"
        :successText="justification.textoSucesso"
      />
    </b-modal>

    <b-modal
      has-modal-card
      :active.sync="batchConfirmation.exibir"
      :onCancel="closeBatchConfirmation"
      @confirm="confirmBatchQualification"
    >
      <AppBatchQualificationConfirmation
        v-if="batchConfirmation.exibir"
        :confirm-text="batchConfirmation.confirmText"
        :habilitar="batchConfirmation.habilitar"
        :items="batchConfirmationItems"
        :loading="loading.batch"
        :mobile="mobile"
        :scope-label="batchScopeLabel"
        :supplier-label="batchSupplierLabel"
        :title="batchConfirmation.title"
      />
    </b-modal>
  </div>
</template>

<script>
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import { mapActions, mapGetters, mapState } from 'vuex';
import saveAs from 'save-as';

import formMixin from '@/mixins/formulario';
import { fileServer } from '@/utils/functions';
import { funcoes as functions } from '@/utils/enumerados';
import { formatarData as formatDate } from '@/utils/format';
import { uploadLimit } from '@/utils/data';
import sanitizeFileName from '@/utils/sanitizes';

import AppJustification from '@/components/AppJustification';
import AppBatchQualificationConfirmation from '@/components/AppBatchQualificationConfirmation';

export default {
  name: 'ListFiles',
  components: { AppJustification, AppBatchQualificationConfirmation },
  props: {
    item: Object,
    qualificationCompany: Object,
    situation: Number,
    judgmentDiligence: {
      type: Object,
      default: null,
    },
  },
  mixins: [formMixin],
  data() {
    const data = {
      files: [],
      name: null,
    };
    const disable = {
      accept: false,
      delete: false,
      refuse: false,
      resetEvaluation: false,
    };
    const justification = {
      textoSucesso: '',
      textoFalha: '',
      exibirSucesso: false,
      exibirFalha: false,
      exibir: false,
    };
    const batchConfirmation = {
      confirmText: '',
      exibir: false,
      habilitar: true,
      title: '',
    };
    const loading = {
      downloadZip: false,
      loadPage: false,
      send: false,
      batch: false,
    };

    return {
      action: '',
      batchConfirmation,
      data,
      disable,
      documents: [],
      justification,
      loading,
      loaded: false,
      selectedTypes: [],
    };
  },
  filters: {
    fileServer,
    formatDate,
  },
  computed: {
    ...mapState('app', ['windowWidth']),
    ...mapState('auctionsDocuments', ['auctionDocuments', 'auctionUpload']),
    ...mapState('judgmentDiligences', ['uploadProgress']),
    ...mapState('companies', ['company']),
    ...mapState('companiesDocuments', ['companyDocuments']),
    ...mapState('documentTypes', ['documentTypes']),
    ...mapState('proposals', { proposals: 'proposals' }),
    ...mapState('requiredDocuments', ['requiredDocuments']),
    ...mapGetters('app', { mobile: 'mobile' }),
    ...mapState('auctionsBidders', ['userLoggedBidder']),
    ...mapGetters({
      auction: 'obterOfertaCompra',
      bids: 'obterOfertaCompraLances',
      deadlines: 'obterOfertaCompraPrazos',
      function: 'getFunction',
      items: 'obterTodosItens',
    }),

    filteredDocumentTypes() {
      const fixedOptions = this.documentTypes
        .filter(document => {
          if (
            ['Documentos Complementares', 'Declarações', 'Outros Documentos'].includes(
              document.type,
            )
          ) {
            return document;
          }
          return null;
        })
        .filter(Boolean);

      const addUniqueByType = documentTypes =>
        documentTypes.reduce((documents, document) => {
          if (!document || !document.type) return documents;
          const exists = documents.some(element => element.type === document.type);
          if (!exists) documents.push(document);
          return documents;
        }, []);

      // In qualification, bidders can only submit required docs + "Outros Documentos".
      if (this.bidder && this.item?.estado === 'HABILITACAO') {
        const qualificationRequiredDocuments = this.requiredDocuments.filter(
          document => document.habilitacao || document.stage === 'habilitacao',
        );
        const pendingRequiredDocuments = qualificationRequiredDocuments.filter(
          documentType =>
            !this.auctionDocuments.some(
              auctionDocument => auctionDocument.type === documentType.type,
            ),
        );
        const otherDocumentsOption = this.documentTypes.find(
          document => document.type === 'Outros Documentos',
        );

        return addUniqueByType(pendingRequiredDocuments.concat(otherDocumentsOption));
      }

      const filteredDocumentTypes = this.documentTypes
        .filter(documentType => {
          return !this.auctionDocuments.some(auctionDocument => {
            return auctionDocument.type === documentType.type;
          });
        })
        .concat(fixedOptions);

      if (this.proposalStep) {
        return addUniqueByType(
          this.requiredDocuments
            .filter(documentType => {
              return documentType.propostas === true;
            })
            .concat(fixedOptions),
        );
      }

      return addUniqueByType(filteredDocumentTypes);
    },
    currentQualificationDeadline() {
      return (this.deadlines || []).find(
        deadline => Number(deadline.prazo_id) === 11 && !deadline.data_ate,
      );
    },
    isAdhesionQualificationContext() {
      const context = (this.currentQualificationDeadline?.qualification_context || '')
        .toString()
        .toUpperCase();
      if (context === 'ADESAO') {
        return true;
      }
      if (context === 'NORMAL') {
        return false;
      }

      // Legacy fallback for deadlines created before qualificationContext existed.
      const latestClosedAdhesion = [...(this.deadlines || [])]
        .filter(deadline => Number(deadline.prazo_id) === 13 && deadline.data_ate)
        .sort((a, b) => new Date(b.data_ate).getTime() - new Date(a.data_ate).getTime())[0];
      const qualificationStart = new Date(this.currentQualificationDeadline?.data_de).getTime();
      const adhesionEnd = new Date(latestClosedAdhesion?.data_ate).getTime();
      const ONE_SECOND_MS = 1000;

      if (!Number.isFinite(qualificationStart) || !Number.isFinite(adhesionEnd)) {
        return false;
      }

      return Math.abs(qualificationStart - adhesionEnd) <= ONE_SECOND_MS;
    },
    adhesionStep() {
      // True only during Adesão de Preço or Habilitação da Adesão — not merely
      // because the auction once had a prazo 13.
      if (this.situation === 11) {
        return true;
      }
      if (this.situation === 9) {
        return this.isAdhesionQualificationContext;
      }
      return false;
    },
    judgmentDiligenceStep() {
      return !!this.judgmentDiligence;
    },
    uploadDisabled() {
      if (this.judgmentDiligenceStep) return false;
      return this.situation >= 9 && this.item.estado !== 'HABILITACAO';
    },
    uploadProgressValue() {
      return this.judgmentDiligenceStep ? this.uploadProgress : this.auctionUpload;
    },
    sendButtonLabel() {
      if (this.judgmentDiligenceStep) {
        return this.judgmentDiligence.documentsSent
          ? 'Enviar novos documentos'
          : 'Enviar Diligência';
      }
      return 'Enviar';
    },
    displayTableFooter() {
      if (this.judgmentDiligenceStep) {
        return this.bidder && this.documents.length;
      }

      return (
        (this.situation !== 17 || (this.bidder && this.suspendedQualification)) &&
        (this.crier || (this.bidder && this.documents.length))
      );
    },
    proposalStep() {
      return this.auction.situacao.id === 3;
    },
    authority() {
      return this.function === functions.AUTORIDADE;
    },
    bidder() {
      return this.function === functions.LICITANTE;
    },
    calculateButtonsColumn() {
      if (this.bidder && this.documentToSend) return 'column is-two-thirds';
      if (this.bidder) return 'column is-one-third';
      return 'column';
    },
    calculateUploadColumn() {
      if (this.bidder && this.documentToSend) return 'column is-one-third';
      if (this.bidder) return 'column is-two-thirds';
      return 'column';
    },
    complementaryExists() {
      return this.documents.some(document => document.origin !== 'PROPOSTAS');
    },
    crier() {
      return this.function === functions.PREGOEIRO;
    },
    disableIncapacitateButton() {
      return this.item?.estado !== 'HABILITACAO' || this.docWithoutEvaluation;
    },
    disableQualificationButton() {
      return (
        this.item?.estado !== 'HABILITACAO' || !this.documents.length || this.docWithoutEvaluation
      );
    },
    currentBidderId() {
      if (this.qualificationCompany && this.qualificationCompany.bidderId) {
        return this.qualificationCompany.bidderId;
      }
      return this.item && this.item.menor_lance ? this.item.menor_lance.fornecedor_id : null;
    },
    currentBidderAlias() {
      if (this.qualificationCompany && this.qualificationCompany.alias) {
        return this.qualificationCompany.alias;
      }
      return this.item && this.item.menor_lance ? this.item.menor_lance.fornecedor : '';
    },
    currentBidderSocialDenomination() {
      if (this.qualificationCompany && this.qualificationCompany.socialDenomination) {
        return this.qualificationCompany.socialDenomination;
      }
      return this.item && this.item.menor_lance ? this.item.menor_lance.razao_social : '';
    },
    batchEligibleItems() {
      const bidderId = this.currentBidderId;
      if (!bidderId || !this.items || !this.items.length) return [];

      if (this.isPhaseInversionQualification) {
        return this.items.filter(
          item =>
            item.estado === 'HABILITACAO' &&
            this.bids.some(
              bid =>
                bid.ofertaCompraItemId === item.id &&
                bid.fornecedor_id === bidderId &&
                bid.status === 'VALIDO' &&
                bid.habilitacao == null,
            ),
        );
      }

      return this.items.filter(
        item =>
          item.estado === 'HABILITACAO' &&
          item.menor_lance &&
          item.menor_lance.fornecedor_id === bidderId,
      );
    },
    isPhaseInversionQualification() {
      return (
        !!this.auction?.phase_inversion && (this.situation === 9 || this.suspendedQualification)
      );
    },
    displayBatchQualification() {
      return (
        this.crier &&
        !this.judgmentDiligenceStep &&
        !this.adhesionStep &&
        this.batchEligibleItems.length >= 2
      );
    },
    batchConfirmationItems() {
      return this.batchEligibleItems.map(item => ({
        identifier: item.identificador,
        description: (item.item && item.item.descricao) || item.descricao || 'Sem descrição',
      }));
    },
    batchSupplierLabel() {
      return `(${this.currentBidderAlias || 'sem alias'}) - ${
        this.currentBidderSocialDenomination || 'Fornecedor'
      }`;
    },
    batchScopeLabel() {
      return this.isPhaseInversionQualification
        ? 'em todos os itens abaixo em que ele possui oferta pendente de habilitação.'
        : 'em todos os itens abaixo em que ele possui o melhor lance.';
    },
    displayAcceptedColumn() {
      return this.documents.some(document => [true, false].indexOf(document.accept) > -1);
    },
    displayNameColumn() {
      return this.documents.some(
        document =>
          ['Documentos Complementares', 'Declarações', 'Outros Documentos'].indexOf(document.type) >
          -1,
      );
    },
    suspendedQualification() {
      return (
        this.situation === 17 &&
        this.deadlines &&
        this.deadlines[this.deadlines.length - 2]?.prazo_id === 11
      );
    },
    displayOptionsColumn() {
      if (this.judgmentDiligenceStep) {
        if (this.crier) {
          return this.documents.length > 0;
        }

        return this.bidder && this.documentNotAccepted;
      }

      if (this.situation === 17 && !(this.bidder && this.suspendedQualification)) return false;

      if (
        this.situation === 3 &&
        this.bidder &&
        (this.profileDocument || this.documentBeforeSession)
      ) {
        return true;
      }

      return !!(
        (this.crier && this.documentToAccepted) ||
        (this.bidder && this.documentNotAccepted && this.complementaryExists)
      );
    },
    documentBeforeSession() {
      return this.documents.some(document => document.origin && document.origin === 'PROPOSTAS');
    },
    documentNotAccepted() {
      return this.documents.some(document => [null, false].indexOf(document.accept) > -1);
    },
    documentToAccepted() {
      return this.documents.some(document => document.accept === null);
    },
    documentToSend() {
      return this.documents.some(document => !document.createdAt || !document.origin);
    },
    docWithoutEvaluation() {
      return this.documents.some(document => document.accept === null);
    },
    proposalsDocumentTypes() {
      return this.documentTypes
        .filter(document => document.proposals)
        .concat(this.requiredDocuments)
        .reduce((documents, document) => {
          const documentExists = documents.some(element => element.type === document.type);
          if (!documentExists) documents.push(document);
          return documents;
        }, []);
    },
    profileDocument() {
      return this.documents.some(document => !document.origin);
    },
    proposalsDelivery() {
      return this.situation === 3;
    },
    qualificationDocumentTypes() {
      if (this.adhesionStep) {
        return this.documentTypes.filter(
          document => document.qualification && document.type !== 'Proposta Readequada',
        );
      }

      return this.documentTypes.filter(document => document.qualification);
    },
  },
  watch: {
    auctionDocuments() {
      if (this.crier || this.judgmentDiligenceStep) this.loadDocuments();
    },
    bids() {
      if (this.judgmentDiligenceStep) return;

      const enabledItem = this.bids.some(
        bid =>
          bid.ofertaCompraItemId === this.item.id &&
          (['HABILITADO', 'INABILITADO'].indexOf(bid.adesao) > -1 ||
            this.item?.estado !== 'HABILITACAO'),
      );
      if (enabledItem) this.$emitBus('voltar');
    },
    items() {
      if (this.judgmentDiligenceStep) return;

      if (!this.adhesionStep && this.item && this.item?.estado !== 'HABILITACAO') {
        this.$emitBus('voltar');
      }
    },
  },
  sockets: {
    'auction:documents': function updateAuctionDocuments(documents) {
      const updateDocuments = this.verifyUpdateDocuments(documents);
      if (updateDocuments) {
        if (documents && Array.isArray(documents)) {
          const filteredDocuments = this.judgmentDiligenceStep
            ? documents.filter(
                document =>
                  document.origin === 'JULGAMENTO' &&
                  document.diligenciaSolicitacaoId === this.judgmentDiligence.id,
              )
            : documents.filter(
                document => !document.auctionItemId || document.auctionItemId === this.item.id,
              );
          this.updateAuctionDocuments(filteredDocuments);
        } else {
          this.updateAuctionDocuments(documents);
        }
      }
    },
  },
  methods: {
    ...mapActions('auctionsDocuments', [
      'addAuctionDocuments',
      'cleanAuctionDocuments',
      'fetchAuctionBidderDocuments',
      'removeAuctionDocument',
      'resetAuctionDocumentsUpload',
      'updateAuctionDocuments',
    ]),
    ...mapActions('judgmentDiligences', [
      'fetchJudgmentDiligences',
      'fetchUnresolvedJudgmentDiligences',
      'resetJudgmentDiligencesUpload',
      'uploadJudgmentDiligenceDocuments',
    ]),
    ...mapActions('auctionsQualifications', [
      'acceptAuctionDocument',
      'refuseAuctionDocument',
      'resetAuctionDocumentEvaluation',
      'qualifyBidder',
      'disqualifyBidder',
      'evaluateBidderBatch',
    ]),
    ...mapActions(['atualizarHabilitacaoLances']),
    addFiles(files) {
      this.documents = this.documents.concat(
        files.map(file => {
          if (this.adhesionStep) {
            return { fileName: file.name, file, type: 'Documentos Complementares' };
          }
          if (this.judgmentDiligenceStep) {
            return { fileName: file.name, file, type: '' };
          }
          return { fileName: file.name, file };
        }),
      );
      if (this.adhesionStep) {
        const index = this.documents.length - files.length;
        this.selectFirstInput(index);
      }
      this.data.files = [];

      this.resetAuctionDocumentsUpload();
    },
    backToItems() {
      if (this.$refs && this.$refs.justificationModal) {
        this.$refs.justificationModal.close();
      }
      this.$emitBus('voltar');
    },
    customDocument(type) {
      return ['Documentos Complementares', 'Declarações', 'Outros Documentos'].indexOf(type) > -1;
    },
    async deleteDocument(index, documentId) {
      this.selectedTypes.splice(index, 1);
      this.documents.splice(index, 1);
      this.documents = this.documents.slice();

      const exists = this.auctionDocuments.some(document => document.id === documentId);
      if (exists) {
        this.deleteAuctionDocument(documentId);
      }
    },
    async deleteAuctionDocument(documentId) {
      this.disable.delete = true;
      try {
        await this.removeAuctionDocument({
          auctionId: this.auction.id,
          bidderId: this.userLoggedBidder.id,
          documentId,
        });
        this.$alerta('Documento excluído', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao excluir documento', 'is-danger');
      }
      this.disable.delete = false;
    },
    displayDeleteButton(row) {
      if (this.situation === 3 && (row.origin === 'PROPOSTAS' || !row.origin)) return true;
      if (!this.crier && !row.accept && ['PROPOSTAS'].indexOf(row.origin) === -1) {
        return true;
      }
      return false;
    },
    async downloadDocumentsZip() {
      const zip = new JSZip();
      const zipFilename = `${sanitizeFileName(this.qualificationCompany.socialDenomination)}.zip`;
      let count = 0;
      let errorCount = 0;

      this.loading.downloadZip = true;
      const promises = this.documents.map(async (document, index) => {
        try {
          const file = await JSZipUtils.getBinaryContent(fileServer(document.file));
          const fileExtension = document.file.split('.').pop();
          zip.file(
            `${this.formatDocumentName(this.documents, index, document)}.${fileExtension}`,
            file,
            {
              binary: true,
            },
          );
          count += 1;

          if (count === this.documents.length) {
            zip.generateAsync({ type: 'blob' }).then(content => {
              saveAs(content, zipFilename);
            });
          }
        } catch (_) {
          errorCount += 1;
        }
      });

      await Promise.all(promises);

      if (errorCount) this.$alerta('Erro no download', 'is-danger');

      this.loading.downloadZip = false;
    },
    async evaluateBidder({ justification, state }) {
      const enableBidder = state === 'success';

      const data = {
        itemId: this.item.id,
        bidder: this.qualificationCompany
          ? this.qualificationCompany.alias
          : this.item.menor_lance.fornecedor,
        justification: enableBidder ? justification || 'Habilitada.' : justification,
      };

      try {
        if (enableBidder) {
          await this.qualifyBidder(data);
        } else {
          await this.disqualifyBidder(data);
        }
        this.atualizarHabilitacaoLances({
          itemId: this.item.id,
          fornecedorId: this.currentBidderId,
          habilitacao: enableBidder,
        });
        this.$alerta(`Licitante ${enableBidder ? 'habilitado' : 'inabilitado'}`, 'is-success');
      } catch (error) {
        this.$alerta(
          `Erro ao ${enableBidder ? 'habilitar' : 'inabilitar'} o licitante`,
          'is-danger',
        );
      }

      this.documents = this.auctionDocuments.slice();
      this.backToItems();
    },
    async evaluateBidderBatchAction({ habilitar, justification }) {
      if (this.loading.batch) return false;

      this.loading.batch = true;
      try {
        const result = await this.evaluateBidderBatch({
          bidderId: this.currentBidderId,
          habilitar,
          justification,
        });
        const processed = (result && result.processados) || [];
        if (processed.length) {
          this.atualizarHabilitacaoLances(
            processed.map(itemId => ({
              itemId,
              fornecedorId: this.currentBidderId,
              habilitacao: habilitar,
            })),
          );
          this.$alerta('Ação aplicada com sucesso', 'is-success');
        } else {
          this.$alerta(
            'Nenhum item foi processado. Verifique se os documentos estão avaliados.',
            'is-warning',
          );
        }
        this.documents = this.auctionDocuments.slice();
        this.backToItems();
        return true;
      } catch (error) {
        this.$alerta(`Erro ao ${habilitar ? 'habilitar' : 'inabilitar'} todos`, 'is-danger');
        return false;
      } finally {
        this.loading.batch = false;
      }
    },
    async evaluateDocument(documentId, action) {
      const acceptAction = action === 'accept';

      this.disable.accept = true;
      this.disable.refuse = true;
      try {
        if (acceptAction) {
          await this.acceptAuctionDocument({
            auctionId: this.auction.id,
            bidderId: this.qualificationCompany.bidderId,
            documentId,
          });
        } else {
          await this.refuseAuctionDocument({
            auctionId: this.auction.id,
            bidderId: this.qualificationCompany.bidderId,
            documentId,
          });
        }
        this.$alerta(`Documento ${acceptAction ? 'aceito' : 'recusado'}`, 'is-success');
        if (this.judgmentDiligenceStep) {
          await Promise.all([
            this.fetchJudgmentDiligences({ auctionId: this.auction.id, activeOnly: true }),
            this.fetchUnresolvedJudgmentDiligences({ auctionId: this.auction.id }),
          ]);
        }
        this.loadDocuments();
      } catch (error) {
        const message =
          (error && error.response && error.response.data && error.response.data.details
            ? error.response.data.details.message
            : null) || `Erro ao ${acceptAction ? 'aceitar' : 'recusar'} documento`;
        this.$alerta(message, 'is-danger');
      }
      this.disable.accept = false;
      this.disable.refuse = false;
    },
    async resetDocumentEvaluation(documentId) {
      this.disable.resetEvaluation = true;
      try {
        await this.resetAuctionDocumentEvaluation({
          auctionId: this.auction.id,
          bidderId: this.qualificationCompany.bidderId,
          documentId,
        });
        this.$alerta('Avaliação do documento desfeita', 'is-success');
        await Promise.all([
          this.fetchJudgmentDiligences({ auctionId: this.auction.id, activeOnly: true }),
          this.fetchUnresolvedJudgmentDiligences({ auctionId: this.auction.id }),
        ]);
        this.loadDocuments();
      } catch (error) {
        const message =
          (error && error.response && error.response.data && error.response.data.details
            ? error.response.data.details.message
            : null) || 'Erro ao desfazer avaliação do documento';
        this.$alerta(message, 'is-danger');
      }
      this.disable.resetEvaluation = false;
    },
    formatDocumentName(companyDocuments, index, document) {
      const isReadjustedProposal = document.type === 'Proposta Readequada';
      const notReadjustedProposal = companyDocuments.filter(
        element => element.type === 'Proposta Readequada',
      );
      const getReadjustedProposalIndex = documentId =>
        notReadjustedProposal.findIndex(element => element.id === documentId) + 1;

      return `${index + 1}-${sanitizeFileName(document.type.replaceAll('/', ' '))}${
        isReadjustedProposal ? `-Item-${getReadjustedProposalIndex(document.id)}` : ''
      }`;
    },
    loadDocuments() {
      if (this.judgmentDiligenceStep) {
        this.documents = this.auctionDocuments.filter(
          document =>
            document.origin === 'JULGAMENTO' &&
            document.diligenciaSolicitacaoId === this.judgmentDiligence.id,
        );
        return;
      }

      this.documents = this.auctionDocuments.slice();
    },
    openModal(action) {
      this.action = action;

      if (action === 'habilitar-lote' || action === 'inabilitar-lote') {
        const habilitar = action === 'habilitar-lote';
        this.batchConfirmation = {
          confirmText: habilitar ? 'Habilitar em Todos os Itens' : 'Inabilitar em Todos os Itens',
          exibir: true,
          habilitar,
          title: habilitar ? 'Habilitar em Todos os Itens' : 'Inabilitar em Todos os Itens',
        };
        return;
      }

      if (action === 'habilitar') {
        this.justification.textoSucesso = 'Habilitar';
        this.justification.exibirSucesso = true;
        this.justification.exibirFalha = false;
      } else if (action === 'inabilitar') {
        this.justification.textoFalha = 'Inabilitar';
        this.justification.exibirSucesso = false;
        this.justification.exibirFalha = true;
      }
      this.justification.exibir = true;
    },
    closeBatchConfirmation() {
      this.batchConfirmation.exibir = false;
    },
    async confirmBatchQualification({ justificativa }) {
      const success = await this.evaluateBidderBatchAction({
        habilitar: this.batchConfirmation.habilitar,
        justification: justificativa,
      });
      if (success) this.closeBatchConfirmation();
    },
    async saveDocuments() {
      if (this.judgmentDiligenceStep) {
        await this.saveJudgmentDiligenceDocuments();
        return;
      }

      if (this.validate()) {
        const data = new FormData();

        const documentsToSend = this.documents.slice(this.auctionDocuments.length);
        this.selectedTypes = this.selectedTypes.slice(this.auctionDocuments.length);

        data.append('types', JSON.stringify(this.selectedTypes));

        const validities = [];
        const profileDocuments = [];
        const newDocuments = [];
        documentsToSend.forEach(document => {
          if (typeof document.file !== 'string') {
            newDocuments.push(document.file);
          } else {
            profileDocuments.push(document);
          }
          validities.push(document.validity);
        });

        if (profileDocuments.length) {
          data.append('files', JSON.stringify(profileDocuments));
        }
        if (newDocuments.length) {
          newDocuments.forEach(document => {
            data.append('files', document);
          });
        }
        data.append('validities', JSON.stringify(validities));
        data.append('itemId', JSON.stringify(this.item ? this.item.id : null));

        this.disable.delete = true;
        this.loading.send = true;
        try {
          await this.addAuctionDocuments({
            auctionId: this.auction.id,
            bidderId: this.userLoggedBidder.id,
            data,
          });

          this.$alerta('Documento(s) adicionado(s)', 'is-success');
          this.documents = this.auctionDocuments.slice();
          this.data.files = [];
          this.selectedTypes = [];
        } catch (error) {
          const customError = error.response.data;
          if (
            error.response.status === 400 &&
            customError &&
            customError.details.message === 'Fora do prazo'
          ) {
            this.$alerta('Fora do prazo', 'is-danger');
          } else {
            this.$alerta('Erro ao adicionar documento(s)', 'is-danger');
          }
        }
        this.loading.send = false;
        this.disable.delete = false;
      }
    },
    async saveJudgmentDiligenceDocuments() {
      const documentsToSend = this.documents.filter(document => !document.createdAt);

      if (!documentsToSend.length) {
        this.$alerta('Adicione ao menos um documento', 'is-danger');
        return;
      }

      if (documentsToSend.some(document => !document.type || !document.type.trim())) {
        this.$alerta('Informe o nome de cada documento', 'is-danger');
        return;
      }

      const data = new FormData();
      data.append('types', JSON.stringify(documentsToSend.map(document => document.type.trim())));
      documentsToSend.forEach(document => {
        data.append('files', document.file);
      });

      this.disable.delete = true;
      this.loading.send = true;
      try {
        await this.uploadJudgmentDiligenceDocuments({
          auctionId: this.auction.id,
          diligenceId: this.judgmentDiligence.id,
          data,
        });
        await Promise.all([
          this.fetchJudgmentDiligences({
            auctionId: this.auction.id,
            activeOnly: true,
          }),
          this.fetchUnresolvedJudgmentDiligences({ auctionId: this.auction.id }),
          this.fetchAuctionBidderDocuments({
            auctionId: this.auction.id,
            bidderId: this.qualificationCompany.bidderId,
            origin: 'JULGAMENTO',
          }),
        ]);
        this.loadDocuments();
        this.data.files = [];
        this.selectedTypes = [];
        this.$alerta('Documentos enviados com sucesso', 'is-success');
      } catch (error) {
        const message =
          (error && error.response && error.response.data && error.response.data.details
            ? error.response.data.details.message
            : null) || 'Erro ao enviar diligência';
        this.$alerta(message, 'is-danger');
      }
      this.loading.send = false;
      this.disable.delete = false;
    },
    selectFirstInput(index) {
      setTimeout(() => {
        this.$refs[`name-${index}`].focus();
      }, 100);
    },
    selectType(index, type) {
      if (type) {
        this.selectedTypes[index] = type;
        this.documents[index].type = type;
        this.documents = this.documents.slice();
      }

      if (this.customDocument(type)) {
        this.selectFirstInput(index);
      }
    },
    updateTypeName(index, typeOrigin, typeName) {
      const uppercaseTypeName = `${typeName.toUpperCase()} (${typeOrigin})`;
      if (typeName) {
        this.documents[index].typeName = uppercaseTypeName;
        this.selectedTypes[index] = uppercaseTypeName;
      }
    },
    validate() {
      if (this.judgmentDiligenceStep) {
        return true;
      }

      if (!this.documents.length || !this.filteredDocumentTypes.length) {
        this.$alerta('Adicione um ou mais documentos', 'is-danger');
        return false;
      }

      if (this.$refs) {
        const missedNames = Object.keys(this.$refs).filter(
          key => key.startsWith('name-') && this.$refs[key] && !this.$refs[key].value,
        );
        if (missedNames.length) {
          this.$alerta('Existe documento sem nome', 'is-danger');
          return false;
        }
      }

      const valid = this.documents.filter(element => !element.type);
      if (valid.length) {
        this.$alerta('Selecione o tipo de cada documento', 'is-danger');
        return false;
      }

      const mappedDocuments = this.documents.map(
        document => document.typeName || document.name || document.type,
      );
      const documentRepeat = mappedDocuments.some(
        (document, index) => mappedDocuments.indexOf(document) !== index,
      );

      if (documentRepeat) {
        this.$alerta('Tipo de documento deve ser único', 'is-danger', 3000);
        return false;
      }

      if (this.documents.some(document => !document.status && document.file.size > uploadLimit)) {
        this.$alerta('Insira arquivo(s) com até 50MB', 'is-danger');
        return false;
      }

      return true;
    },
    validDocument(date) {
      const now = new Date();
      const documentDate = new Date(date);
      return now <= documentDate;
    },
    verifyUpdateDocuments(documents) {
      if (this.judgmentDiligenceStep) {
        const matchesDiligence = document =>
          document.origin === 'JULGAMENTO' &&
          document.diligenciaSolicitacaoId === this.judgmentDiligence.id;

        if (documents && Array.isArray(documents)) {
          return documents.some(matchesDiligence);
        }

        if (documents && typeof documents === 'object') {
          return matchesDiligence(documents);
        }

        return false;
      }

      if (this.crier && documents && Array.isArray(documents)) {
        if (documents[0].bidderId === this.qualificationCompany.bidderId) return true;
      }
      if (this.crier && documents && typeof documents === 'object') {
        if (documents.bidderId === this.qualificationCompany.bidderId) return true;
      }
      if ([true, false].indexOf(documents.accept) > -1) return true;

      return false;
    },
  },
  async created() {
    if (this.judgmentDiligenceStep) {
      this.loading.loadPage = true;
      try {
        await this.fetchAuctionBidderDocuments({
          auctionId: this.auction.id,
          bidderId: this.qualificationCompany.bidderId,
          origin: 'JULGAMENTO',
        });
      } catch (error) {
        this.$alerta('Erro ao carregar documentos', 'is-danger');
      }
      this.loading.loadPage = false;
      this.resetJudgmentDiligencesUpload();
    }

    this.loadDocuments();
    this.resetAuctionDocumentsUpload();
  },
};
</script>

<style lang="scss" scoped>
.actions-buttons {
  margin-left: 0.2rem;
  margin-bottom: 0.2rem;
}

.column-document {
  width: 10vw;
}

.column-type,
.column-name {
  max-width: 30vw;
}

.custom-tag {
  font-size: 0.7rem;
}

.custom-upload {
  margin-bottom: 1.5rem;
  display: block;
}

.custom-upload-content {
  padding: 0.8rem;
}

.custom-upload-icon {
  margin: 0.4rem;
}

.delete-button {
  margin-left: 0.2rem;
}

.documents-table {
  margin: 0.5rem 0;
  font-size: 0.92rem;
}

.document-type {
  white-space: normal;
}

.documents-warning-tag {
  margin-right: 30%;
}

.empty-panel {
  justify-content: center !important;
}

.footer-elements {
  margin-top: 1.5rem;
}

.options-column {
  justify-content: center;
  min-width: fit-content;
}

.select-type {
  text-align: left;
}

.sticky-content {
  position: -webkit-sticky;
  position: sticky;
  top: 1rem;
}

.tooltips-tag {
  margin-right: 0.2rem;
}

@media (max-width: 1366px) {
  .column-type,
  .column-name {
    max-width: 20vw;
  }

  .documents-table {
    font-size: 0.88rem;
  }
}

@media (max-width: 961px) {
  .custom-column {
    width: unset !important;
  }

  .is-column-reverse-custom {
    display: flex;
    flex-direction: column-reverse;
  }

  .reset-width {
    width: unset !important;
  }
}

@media (max-width: 769px) {
  .column-type,
  .column-name {
    max-width: 90vw;
  }

  .input-name,
  .select-type {
    width: 60vw;
  }
}
</style>

<style lang="scss">
.documents-table .table-container {
  overflow: unset;
}

.documents-table .table-wrapper .table {
  max-width: 98.4%;
}

.select-type > div > input {
  caret-color: transparent;
  cursor: pointer;
}

.qualification-actions__split {
  display: inline-flex;
  align-items: stretch;
}

.qualification-actions__split .button {
  margin-bottom: 0;
  margin-top: 0;
  height: 2.25em;
}

.qualification-actions__menu {
  display: inline-flex;
  align-items: stretch;
}

.qualification-actions__menu .dropdown-trigger {
  display: inline-flex;
  align-items: stretch;
}

.qualification-actions__split .qualification-actions__main.button {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.qualification-actions__split .qualification-actions__toggle.button {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  margin-left: -1px;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}

.qualification-actions__menu .dropdown-content .dropdown-item {
  text-align: left;
}
</style>
